import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";

export type UserRole = "superadmin" | "admin" | "user";

export const useUserRole = () => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchUserRoles = async () => {
      try {
        const user = await getSessionUser();
        if (cancelled) return;

        if (!user) {
          setRoles([]);
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (cancelled) return;
        if (error) {
          console.error("Error fetching user roles:", error);
          setRoles([]);
        } else {
          setRoles(data?.map((r) => r.role as UserRole) || []);
        }
      } catch (error) {
        if (!cancelled) console.error("Error in fetchUserRoles:", error);
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUserRoles();
    return () => { cancelled = true; };
  }, []);

  const hasRole = (role: UserRole) => roles.includes(role);
  const isSuperAdmin = hasRole("superadmin");
  const isAdmin = hasRole("admin") || isSuperAdmin;

  return {
    roles,
    isLoading,
    hasRole,
    isSuperAdmin,
    isAdmin,
  };
};
